/**
 * Guest (email-first) training geo alert helpers shared by API + renderer.
 * Pure functions only — no Prisma, no crypto secrets.
 */

/** Confirm links are single-use and expire; T1 uses 48h. */
export const TRAINING_GEO_ALERT_GUEST_CONFIRM_TTL_HOURS = 48;

/**
 * `geo_county_id` sentinel for state-wide rows. MySQL unique indexes do not dedupe
 * NULLs, so the column is NOT NULL and state-wide rows store 0.
 */
export const TRAINING_GEO_ALERT_GUEST_STATE_WIDE_COUNTY_ID = 0;

/** T1 shipped state-wide only; T2 adds optional county rows. */
export type TrainingGeoAlertGuestScope = 'state_wide' | 'county';

export const TRAINING_GEO_ALERT_GUEST_SCOPE: TrainingGeoAlertGuestScope = 'state_wide';

export const TRAINING_GEO_ALERT_GUEST_COUNTY_SCOPE: TrainingGeoAlertGuestScope = 'county';

/** Upper bound on counties accepted per subscribe call (one row is written per county). */
export const TRAINING_GEO_ALERT_GUEST_MAX_COUNTIES = 25;

export const TRAINING_GEO_ALERT_GUEST_SOURCE_MAX_LENGTH = 64;

const GUEST_EMAIL_MAX_LENGTH = 255;
const GUEST_EMAIL_SHAPE = /^[^\s@,;:<>"'()[\]\\]+@[^\s@.,;:<>"'()[\]\\]+(\.[^\s@.,;:<>"'()[\]\\]+)+$/;

/** Lowercase + trim. Returns null when the value cannot be an email address. */
export function normalizeGuestAlertEmail(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.trim().toLowerCase();
  if (normalized.length < 5 || normalized.length > GUEST_EMAIL_MAX_LENGTH) return null;
  if (!GUEST_EMAIL_SHAPE.test(normalized)) return null;
  return normalized;
}

/** Free-text campaign/source label, clipped to the column width. */
export function normalizeGuestAlertSource(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim().slice(0, TRAINING_GEO_ALERT_GUEST_SOURCE_MAX_LENGTH);
  return trimmed.length > 0 ? trimmed : null;
}

export type GuestAlertSubscribeInput = {
  email: string;
  geoStateId: number;
  /** Empty means state-wide; otherwise one confirmed-pending county row per id. */
  countyIds: number[];
  source: string | null;
};

export type GuestAlertSubscribeParse =
  | { ok: true; value: GuestAlertSubscribeInput }
  | { ok: false; reason: 'invalid_email' | 'invalid_state' };

/**
 * Positive integer county ids, deduped, ordered, and capped. Non-array or fully
 * unusable input yields an empty list, which callers treat as state-wide.
 */
export function normalizeGuestAlertCountyIds(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<number>();
  for (const entry of raw) {
    const id = typeof entry === 'number' ? entry : Number.parseInt(String(entry ?? ''), 10);
    if (!Number.isInteger(id) || id < 1) continue;
    seen.add(id);
    if (seen.size >= TRAINING_GEO_ALERT_GUEST_MAX_COUNTIES) break;
  }
  return [...seen].sort((a, b) => a - b);
}

/**
 * Validate a guest subscribe payload without touching the database. Callers still
 * confirm the state and counties exist; this only rejects structurally bad input.
 */
export function parseGuestAlertSubscribeInput(body: {
  email?: unknown;
  geo_state_id?: unknown;
  county_ids?: unknown;
  source?: unknown;
}): GuestAlertSubscribeParse {
  const email = normalizeGuestAlertEmail(body.email);
  if (!email) return { ok: false, reason: 'invalid_email' };

  const geoStateId =
    typeof body.geo_state_id === 'number'
      ? body.geo_state_id
      : Number.parseInt(String(body.geo_state_id ?? ''), 10);
  if (!Number.isInteger(geoStateId) || geoStateId < 1) {
    return { ok: false, reason: 'invalid_state' };
  }

  return {
    ok: true,
    value: {
      email,
      geoStateId,
      countyIds: normalizeGuestAlertCountyIds(body.county_ids),
      source: normalizeGuestAlertSource(body.source),
    },
  };
}

/** The rows a single subscribe call should own for one email + state. */
export type GuestAlertSubscribeTarget = {
  scope: TrainingGeoAlertGuestScope;
  geoCountyId: number;
};

/**
 * County selections become one `county` row each; an empty selection stays a single
 * `state_wide` row on the `geo_county_id = 0` sentinel.
 */
export function guestAlertSubscribeTargets(countyIds: number[]): GuestAlertSubscribeTarget[] {
  if (countyIds.length === 0) {
    return [
      {
        scope: TRAINING_GEO_ALERT_GUEST_SCOPE,
        geoCountyId: TRAINING_GEO_ALERT_GUEST_STATE_WIDE_COUNTY_ID,
      },
    ];
  }
  return countyIds.map((geoCountyId) => ({
    scope: TRAINING_GEO_ALERT_GUEST_COUNTY_SCOPE,
    geoCountyId,
  }));
}

export type GuestAlertMergeRow = {
  geo_state_id: number;
  subscription_scope: TrainingGeoAlertGuestScope;
  geo_county_id: number;
};

export type GuestAlertMergeTarget = {
  geoStateId: number;
  geoCountyId: number;
};

/**
 * Signed-in subscriptions are county-only, so a merged guest row has to become one
 * county row per area: `county` rows map 1:1, `state_wide` rows fan out across every
 * active county in that state. Deduped and stable-ordered; pure so it can be unit tested.
 */
export function expandGuestRowsToUserCountyTargets(
  rows: GuestAlertMergeRow[],
  activeCountyIdsByState: Map<number, number[]> | Record<number, number[]>,
): GuestAlertMergeTarget[] {
  const countiesFor = (stateId: number): number[] => {
    const list =
      activeCountyIdsByState instanceof Map
        ? activeCountyIdsByState.get(stateId)
        : activeCountyIdsByState[stateId];
    return Array.isArray(list) ? list : [];
  };

  const seen = new Set<string>();
  const targets: GuestAlertMergeTarget[] = [];
  const push = (geoStateId: number, geoCountyId: number) => {
    if (!Number.isInteger(geoStateId) || geoStateId < 1) return;
    if (!Number.isInteger(geoCountyId) || geoCountyId < 1) return;
    const key = `${geoStateId}:${geoCountyId}`;
    if (seen.has(key)) return;
    seen.add(key);
    targets.push({ geoStateId, geoCountyId });
  };

  for (const row of rows) {
    if (row.subscription_scope === 'county') {
      push(row.geo_state_id, row.geo_county_id);
      continue;
    }
    for (const countyId of countiesFor(row.geo_state_id)) {
      push(row.geo_state_id, countyId);
    }
  }

  return targets;
}

export type GuestAlertLandingOutcome =
  | 'confirmed'
  | 'confirm_failed'
  | 'unsubscribed'
  | 'unsubscribe_failed';

/** Where the public confirm/unsubscribe redirects land, so API + tests agree. */
export function guestAlertLandingPath(outcome: GuestAlertLandingOutcome): string {
  switch (outcome) {
    case 'confirmed':
      return '/alerts?confirmed=1';
    case 'confirm_failed':
      return '/alerts?confirm=failed';
    case 'unsubscribed':
      return '/alerts?unsubscribed=1';
    case 'unsubscribe_failed':
      return '/alerts?unsubscribe=failed';
    default: {
      const exhaustive: never = outcome;
      return exhaustive;
    }
  }
}

export const TRAINING_GEO_ALERT_GUEST_API_BASE = '/api/events/training-geo-alerts/guest';

export function guestAlertConfirmPath(rawToken: string): string {
  return `${TRAINING_GEO_ALERT_GUEST_API_BASE}/confirm?token=${encodeURIComponent(rawToken)}`;
}

export function guestAlertUnsubscribePath(subscriptionId: number | bigint, signature: string): string {
  return `${TRAINING_GEO_ALERT_GUEST_API_BASE}/unsubscribe?id=${encodeURIComponent(
    String(subscriptionId),
  )}&sig=${encodeURIComponent(signature)}`;
}
